// The "most beloved ship" picker: the current pick with a Clear link, a
// search box and every ship in SHIPS grouped by rate. Used on a member's own
// profile (/cave/profile) and on their admin page (/cave/crew/:slug).
import { useMemo, useState } from 'react';
import type { Ship } from '@clan-heart/shared';
import { SHIPS, rateLabel } from '@clan-heart/shared';
import styles from './ShipPicker.module.css';

function groupShipsByRate(ships: readonly Ship[]): Map<number, Ship[]> {
  const byRate = new Map<number, Ship[]>();
  for (const ship of ships) {
    const list = byRate.get(ship.rate);
    if (list) list.push(ship);
    else byRate.set(ship.rate, [ship]);
  }
  return byRate;
}

const RATES = [...groupShipsByRate(SHIPS).keys()].sort((a, b) => a - b);

/** The search box's filtered, re-grouped view of SHIPS — recomputed per
 * keystroke from the fixed 64-ship list, never from the network. */
function shipGroupsFor(search: string): Array<{ rate: number; ships: Ship[] }> {
  const q = search.trim().toLowerCase();
  const matches = q ? SHIPS.filter((s) => s.name.toLowerCase().includes(q)) : SHIPS;
  const grouped = groupShipsByRate(matches);
  return RATES.map((rate) => ({ rate, ships: grouped.get(rate) ?? [] })).filter((g) => g.ships.length > 0);
}

export function ShipPicker({ value, onChange }: { value: string | null; onChange: (shipId: string | null) => void }) {
  const [search, setSearch] = useState('');
  const shipGroups = useMemo(() => shipGroupsFor(search), [search]);
  const current = value ? SHIPS.find((s) => s.id === value) : undefined;

  return (
    <>
      {current && (
        <p className={styles.currentShip}>
          {current.name} — {rateLabel(current.rate)} · {current.decks} decks · {current.guns} guns{' '}
          <button type="button" className={styles.clearLink} onClick={() => onChange(null)}>
            Clear
          </button>
        </p>
      )}
      <input
        type="search"
        className={styles.search}
        placeholder="Search ships…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        aria-label="Search ships"
      />
      <div className={styles.shipGroups}>
        {shipGroups.length === 0 && <p className={styles.empty}>No ships match “{search}”.</p>}
        {shipGroups.map((group) => (
          <div key={group.rate} className={styles.shipGroup}>
            <h3 className={styles.rateHeading}>{rateLabel(group.rate)}</h3>
            <div className={styles.shipList} role="radiogroup" aria-label={`${rateLabel(group.rate)} ships`}>
              {group.ships.map((ship) => (
                <button
                  key={ship.id}
                  type="button"
                  role="radio"
                  aria-checked={value === ship.id}
                  className={value === ship.id ? `${styles.shipTile} ${styles.shipTileSelected}` : styles.shipTile}
                  onClick={() => onChange(ship.id)}
                >
                  <span className={styles.shipName}>{ship.name}</span>
                  <span className={styles.shipMeta}>
                    {rateLabel(ship.rate)} · {ship.decks} decks · {ship.guns} guns
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
